import React, { useState, useEffect } from 'react';
import { GoogleMap, useLoadScript, Circle, Marker } from '@react-google-maps/api';
import { adminSettingsAPI } from '../services/api';
import { Plus, Trash2, MapPin, Save, X, Loader, RefreshCw } from 'lucide-react';
import MapPicker from './MapPicker';
import { X as XIcon } from 'lucide-react';
import LocationSearchInputBackend from './LocationSearchInputBackend';

const libraries: ("places")[] = ["places"];

const mapContainerStyle = {
  width: '100%',
  height: '400px',
};

const defaultCenter = {
  lat: 28.6139, // Delhi
  lng: 77.2090,
};

interface CoverageArea {
  cityName: string;
  centerPoint: {
    latitude: number;
    longitude: number;
  };
  radiusKm: number;
}

const CityCoverageManager: React.FC = () => {
  const [cities, setCities] = useState<string[]>([]);
  const [coverageAreas, setCoverageAreas] = useState<CoverageArea[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newCityName, setNewCityName] = useState('');
  const [editingArea, setEditingArea] = useState<number | null>(null);
  const [showMapPickerModal, setShowMapPickerModal] = useState<number | null>(null);
  const [searchAddress, setSearchAddress] = useState<string>('');
  const [mapPickerKey, setMapPickerKey] = useState<number>(0); // Key to force MapPicker remount when location changes
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(null);
  
  // Pending coverage area (not added until confirmed)
  const [pendingCoverageArea, setPendingCoverageArea] = useState<CoverageArea | null>(null);
  
  // Separate state for current combined coverage (fetched from database)
  const [currentCoverageAreas, setCurrentCoverageAreas] = useState<CoverageArea[]>([]);
  const [loadingCurrentCoverage, setLoadingCurrentCoverage] = useState(false);
  
  // Expansion recommendations state
  const [recommendations, setRecommendations] = useState<Array<{
    centerLat: number;
    centerLng: number;
    requestCount: number;
    estimatedMonthlyBookings: number;
    nearestCity?: string;
    avgDistanceToNearest?: number;
    sampleAddress?: string;
    lastRequestDate: Date;
  }>>([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [totalRejectedRequests, setTotalRejectedRequests] = useState(0);

  // Rejected locations view state
  const [rejectedLocationsView, setRejectedLocationsView] = useState<'dots' | 'bubbles'>('dots');
  const [bubbleRadiusKm, setBubbleRadiusKm] = useState(5);
  const [rejectedLocationsRaw, setRejectedLocationsRaw] = useState<Array<{
    latitude: number;
    longitude: number;
    address?: string;
    requestedAt: Date;
    source?: 'ios' | 'android' | 'web';
    nearestCity?: string;
    distanceToNearest?: number;
  }>>([]);
  const [rejectedLocationsBubbles, setRejectedLocationsBubbles] = useState<Array<{
    cityName: string;
    centerLat: number;
    centerLng: number;
    requestCount: number;
    radiusKm: number;
  }>>([]);
  const [loadingRejectedLocations, setLoadingRejectedLocations] = useState(false);
  
  // Map zoom and cache state
  const [coverageMapZoomLevel, setCoverageMapZoomLevel] = useState<'city' | 'state'>('city');
  const [rejectedMapZoomLevel, setRejectedMapZoomLevel] = useState<'city' | 'state'>('city');
  const [coverageMapCache, setCoverageMapCache] = useState<any>(null);
  const [rejectedMapCache, setRejectedMapCache] = useState<any>(null);

  const mapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: mapsApiKey,
    libraries,
  });

  useEffect(() => {
    fetchData();
    fetchCurrentCoverage();
    fetchRecommendations();
    // Check cache and fetch rejected locations
    checkCacheAndFetchRejectedLocations();
  }, []);

  // Fetch rejected locations when view mode or zoom level changes
  useEffect(() => {
    // Clear the opposite view data when switching views
    if (rejectedLocationsView === 'dots') {
      setRejectedLocationsBubbles([]); // Clear bubbles when switching to dots
      checkCacheAndFetchRejectedLocations('dots');
    } else {
      setRejectedLocationsRaw([]); // Clear dots when switching to bubbles
      checkCacheAndFetchRejectedLocations('bubbles');
    }
  }, [rejectedLocationsView, rejectedMapZoomLevel, bubbleRadiusKm]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await adminSettingsAPI.getCitiesAndCoverage();
      setCities(response.data?.cities || []);
      setCoverageAreas(response.data?.coverageAreas || []);
    } catch (error: any) {
      console.error('Error fetching cities and coverage:', error);
      setError(error.response?.data?.message || 'Failed to load cities and coverage. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentCoverage = async () => {
    try {
      setLoadingCurrentCoverage(true);
      const response = await adminSettingsAPI.getCitiesAndCoverage();
      setCurrentCoverageAreas(response.data?.coverageAreas || []);
    } catch (error: any) {
      console.error('Error fetching current coverage:', error);
    } finally {
      setLoadingCurrentCoverage(false);
    }
  };

  const fetchRecommendations = async () => {
    try {
      setLoadingRecommendations(true);
      const response = await adminSettingsAPI.getExpansionRecommendations();
      setRecommendations(response.data?.recommendations || []);
      setTotalRejectedRequests(response.data?.totalRejectedRequests || 0);
    } catch (error: any) {
      console.error('Error fetching expansion recommendations:', error);
    } finally {
      setLoadingRecommendations(false);
    }
  };

  const fetchRejectedLocationsRaw = async (forceRefresh = false) => {
    try {
      setLoadingRejectedLocations(true);
      
      // Check cache first (only if not forcing refresh)
      if (!forceRefresh) {
        try {
          const cacheResponse = await adminSettingsAPI.getCachedMap('rejected', rejectedMapZoomLevel, 'dots', false);
          if (cacheResponse.data.cached && cacheResponse.data.mapData) {
            console.log('✅ Using cached rejected locations (dots view)');
            setRejectedLocationsRaw(cacheResponse.data.mapData.locations || []);
            setRejectedMapCache(cacheResponse.data);
            setLoadingRejectedLocations(false);
            return;
          }
        } catch (cacheError) {
          console.log('Cache check failed, fetching fresh data:', cacheError);
        }
      }

      console.log('🔄 Fetching fresh rejected locations (dots view)');
      const response = await adminSettingsAPI.getRejectedLocationsRaw();
      const locations = response.data?.locations || [];
      setRejectedLocationsRaw(locations);
      
      // Save to cache
      try {
        await adminSettingsAPI.saveMapCache('rejected', rejectedMapZoomLevel, { locations }, 'dots');
        console.log('✅ Cached rejected locations (dots view)');
      } catch (cacheError) {
        console.error('Failed to save cache:', cacheError);
      }
    } catch (error: any) {
      console.error('Error fetching raw rejected locations:', error);
    } finally {
      setLoadingRejectedLocations(false);
    }
  };

  const fetchRejectedLocationsBubbles = async (forceRefresh = false) => {
    try {
      setLoadingRejectedLocations(true);
      
      // Check cache first (only if not forcing refresh and radius hasn't changed)
      // Note: Cache key should include radius, but for now we'll invalidate if radius changes
      if (!forceRefresh) {
        try {
          const cacheResponse = await adminSettingsAPI.getCachedMap('rejected', rejectedMapZoomLevel, 'bubbles', false);
          if (cacheResponse.data.cached && cacheResponse.data.mapData) {
            // Check if cached radius matches current radius
            const cachedRadius = cacheResponse.data.mapData.bubbleRadiusKm;
            if (cachedRadius === bubbleRadiusKm) {
              console.log('✅ Using cached rejected locations (bubbles view)');
              setRejectedLocationsBubbles(cacheResponse.data.mapData.bubbles || []);
              setRejectedMapCache(cacheResponse.data);
              setLoadingRejectedLocations(false);
              return;
            } else {
              console.log('🔄 Bubble radius changed, invalidating cache');
            }
          }
        } catch (cacheError) {
          console.log('Cache check failed, fetching fresh data:', cacheError);
        }
      }

      console.log('🔄 Fetching fresh rejected locations (bubbles view)');
      const response = await adminSettingsAPI.getRejectedLocationsBubbleView(bubbleRadiusKm);
      const bubbles = response.data?.bubbles || [];
      setRejectedLocationsBubbles(bubbles);
      
      // Save to cache (include radius in cache data)
      try {
        await adminSettingsAPI.saveMapCache('rejected', rejectedMapZoomLevel, { bubbles, bubbleRadiusKm }, 'bubbles');
        console.log('✅ Cached rejected locations (bubbles view)');
      } catch (cacheError) {
        console.error('Failed to save cache:', cacheError);
      }
    } catch (error: any) {
      console.error('Error fetching bubble view:', error);
    } finally {
      setLoadingRejectedLocations(false);
    }
  };

  const checkCacheAndFetchRejectedLocations = async (viewMode?: 'dots' | 'bubbles') => {
    const mode = viewMode || rejectedLocationsView;
    if (mode === 'dots') {
      await fetchRejectedLocationsRaw();
    } else {
      await fetchRejectedLocationsBubbles();
    }
  };

  const handleForceRefresh = async () => {
    if (rejectedLocationsView === 'dots') {
      await fetchRejectedLocationsRaw(true);
    } else {
      await fetchRejectedLocationsBubbles(true);
    }
  };

  // Calculate color intensity based on request count
  const getBubbleColor = (requestCount: number, maxCount: number): string => {
    if (maxCount === 0) return 'rgba(220, 38, 38, 0.2)';
    const intensity = Math.min(requestCount / maxCount, 1);
    const opacity = 0.2 + (intensity * 0.4); // 0.2 to 0.6 opacity
    const red = Math.floor(220 * intensity); // Darker red for more requests
    return `rgba(${red}, 38, 38, ${opacity})`;
  };

  // Calculate map center and zoom based on zoom level
  const getMapCenterAndZoom = (zoomLevel: 'city' | 'state', items: Array<{ centerLat?: number; centerLng?: number; latitude?: number; longitude?: number }>) => {
    if (items.length === 0) {
      return { center: defaultCenter, zoom: zoomLevel === 'city' ? 10 : 6 };
    }

    let avgLat = 0;
    let avgLng = 0;
    let count = 0;

    items.forEach(item => {
      const lat = item.centerLat || item.latitude;
      const lng = item.centerLng || item.longitude;
      if (lat !== undefined && lng !== undefined) {
        avgLat += lat;
        avgLng += lng;
        count++;
      }
    });

    if (count === 0) {
      return { center: defaultCenter, zoom: zoomLevel === 'city' ? 10 : 6 };
    }

    avgLat /= count;
    avgLng /= count;

    return {
      center: { lat: avgLat, lng: avgLng },
      zoom: zoomLevel === 'city' ? 10 : 6
    };
  };

  const checkCacheAndFetchCoverage = async (forceRefresh = false) => {
    // Coverage map cache check (for future use)
    // For now, just fetch current coverage
    await fetchCurrentCoverage();
  };

  const handleAddCity = () => {
    if (newCityName.trim() && !cities.includes(newCityName.trim())) {
      setCities([...cities, newCityName.trim()]);
      setNewCityName('');
    }
  };

  const handleRemoveCity = (index: number) => {
    const cityName = cities[index];
    setCities(cities.filter((_, i) => i !== index));
    // Also remove coverage area for this city if exists
    setCoverageAreas(coverageAreas.filter(area => area.cityName !== cityName));
  };

  const handleAddCoverageArea = (cityName: string) => {
    const newArea: CoverageArea = {
      cityName,
      centerPoint: { latitude: defaultCenter.lat, longitude: defaultCenter.lng },
      radiusKm: 10
    };
    // Set as pending - will be added to coverageAreas only after confirmation
    setPendingCoverageArea(newArea);
    setEditingArea(null); // No editing area index yet
    setSearchAddress('');
    setMapPickerKey(0);
    setSelectedLocation(null);
    setShowMapPickerModal(-1); // Use -1 to indicate pending area
  };

  const handleUpdateCoverageArea = (index: number, updates: Partial<CoverageArea>) => {
    const updated = [...coverageAreas];
    updated[index] = { ...updated[index], ...updates };
    setCoverageAreas(updated);
  };

  const handleRemoveCoverageArea = (index: number) => {
    setCoverageAreas(coverageAreas.filter((_, i) => i !== index));
    if (editingArea === index) {
      setEditingArea(null);
      setShowMapPickerModal(null);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      // Save cities
      await adminSettingsAPI.updateCities(cities);
      
      // Save coverage areas
      await adminSettingsAPI.updateCoverageAreas(coverageAreas);

      setSuccess('Cities and coverage areas saved successfully!');
      
      // Reload current coverage from database after save
      await fetchCurrentCoverage();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to save cities and coverage areas');
    } finally {
      setSaving(false);
    }
  };

  const handleMapLocationSelect = (index: number, location: { lat: number; lng: number }) => {
    // Update the coverage area with the selected location
    // But don't close the modal - user needs to confirm
    if (index === -1 && pendingCoverageArea) {
      // Updating pending area
      setPendingCoverageArea({
        ...pendingCoverageArea,
        centerPoint: { latitude: location.lat, longitude: location.lng }
      });
    } else {
      // Updating existing area
      handleUpdateCoverageArea(index, {
        centerPoint: { latitude: location.lat, longitude: location.lng }
      });
    }
  };

  const handleConfirmLocation = (index: number) => {
    if (index === -1 && pendingCoverageArea) {
      // Add pending area to coverageAreas only after confirmation
      setCoverageAreas([...coverageAreas, pendingCoverageArea]);
      setPendingCoverageArea(null);
    }
    // Close the modal after user confirms
    setShowMapPickerModal(null);
    setSearchAddress('');
    setSelectedLocation(null);
  };

  const handleCancelLocationSelection = () => {
    // Discard pending area if user closes modal without confirming
    if (showMapPickerModal === -1) {
      setPendingCoverageArea(null);
    }
    setShowMapPickerModal(null);
    setSearchAddress('');
    setSelectedLocation(null);
  };

  const handlePlaceSelected = (place: any) => {
    if (showMapPickerModal !== null && place?.geometry?.location) {
      // Extract lat/lng - handle both function and direct value formats
      let lat: number;
      let lng: number;
      
      if (typeof place.geometry.location.lat === 'function') {
        lat = place.geometry.location.lat();
        lng = place.geometry.location.lng();
      } else {
        lat = place.geometry.location.lat || place.geometry.location.latitude;
        lng = place.geometry.location.lng || place.geometry.location.longitude;
      }
      
      const address = place.formatted_address || place.name || searchAddress;
      
      // Update selected location state
      setSelectedLocation({ lat, lng });
      
      // Update the coverage area with the selected location
      handleMapLocationSelect(showMapPickerModal, { lat, lng });
      
      // Update search address
      setSearchAddress(address);
      
      // Force MapPicker to remount with new location
      setMapPickerKey(prev => prev + 1);
    }
  };

  const getCurrentCoverageAreaForModal = (): CoverageArea | null => {
    if (showMapPickerModal === -1) {
      return pendingCoverageArea;
    }
    if (showMapPickerModal !== null && showMapPickerModal >= 0) {
      return coverageAreas[showMapPickerModal];
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader className="h-6 w-6 animate-spin text-black mr-2" />
        <span className="text-gray-600">Loading cities and coverage...</span>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-red-800">Error loading Google Maps. Please check your API key.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-4">
          <p className="text-green-800">{success}</p>
        </div>
      )}

        {/* Cities Management */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Cities
          </label>
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={newCityName}
              onChange={(e) => setNewCityName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddCity()}
              placeholder="Enter city name (e.g., Ghaziabad)"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
            />
            <button
              onClick={handleAddCity}
              className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Add
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {cities.map((city, index) => (
              <div
                key={index}
                className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full"
              >
                <span className="text-sm text-gray-700">{city}</span>
                <button
                  onClick={() => handleRemoveCity(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Coverage Areas Management */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Coverage Areas
          </label>
          <div className="space-y-4">
            {coverageAreas.map((area, index) => (
              <div key={index} className="border border-gray-300 rounded-md p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center flex-1 mr-2 border border-gray-300 rounded-md bg-white">
                    <input
                      type="text"
                      value={area.cityName}
                      onChange={(e) => handleUpdateCoverageArea(index, { cityName: e.target.value })}
                      className="font-semibold text-gray-800 bg-transparent focus:outline-none px-3 py-2 flex-1"
                      placeholder="Enter area name"
                    />
                    <span className="text-gray-400 mr-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </span>
                  </div>
                  <button
                    onClick={() => handleRemoveCoverageArea(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Center Point</label>
                    <button
                      onClick={() => {
                        setShowMapPickerModal(index);
                        setSearchAddress('');
                        setMapPickerKey(0);
                        setSelectedLocation(null);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center justify-center gap-2"
                    >
                      <MapPin className="h-4 w-4" />
                      Select on Map
                    </button>
                    <p className="text-xs text-gray-500 mt-1">
                      {area.centerPoint.latitude.toFixed(6)}, {area.centerPoint.longitude.toFixed(6)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Radius (km)</label>
                    <input
                      type="number"
                      min="1"
                      value={area.radiusKm}
                      onChange={(e) => handleUpdateCoverageArea(index, { radiusKm: parseFloat(e.target.value) || 1 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4">
            <select
              value=""
              onChange={(e) => {
                if (e.target.value) {
                  handleAddCoverageArea(e.target.value);
                }
              }}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
            >
              <option value="">Add Coverage Area for City...</option>
              {cities
                .filter(city => !coverageAreas.some(area => area.cityName === city))
                .map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
            </select>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <Loader className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Save Changes
              </>
            )}
          </button>
        </div>

        {/* Current Combined Coverage - Separate Section (fetched from database) */}
        {isLoaded && (
          <div className="mt-8 border-t border-gray-300 pt-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Current Combined Coverage</h3>
                <p className="text-sm text-gray-600">
                  This map shows all service coverage areas currently saved in the database. Red circles indicate the service radius for each city.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setCoverageMapZoomLevel('city');
                    // Force refresh cache check
                    checkCacheAndFetchCoverage(true);
                  }}
                  className={`px-3 py-1 text-sm rounded ${
                    coverageMapZoomLevel === 'city'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  City View
                </button>
                <button
                  onClick={() => {
                    setCoverageMapZoomLevel('state');
                    // Force refresh cache check
                    checkCacheAndFetchCoverage(true);
                  }}
                  className={`px-3 py-1 text-sm rounded ${
                    coverageMapZoomLevel === 'state'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  State View
                </button>
              </div>
            </div>
            {loadingCurrentCoverage ? (
              <div className="flex justify-center items-center py-8 border border-gray-300 rounded-md">
                <Loader className="h-6 w-6 animate-spin text-black mr-2" />
                <span className="text-gray-600">Loading coverage map...</span>
              </div>
            ) : currentCoverageAreas.length > 0 ? (
              <div className="border border-gray-300 rounded-md overflow-hidden">
                {(() => {
                  const mapConfig = getMapCenterAndZoom(coverageMapZoomLevel, currentCoverageAreas.map(a => ({ centerLat: a.centerPoint.latitude, centerLng: a.centerPoint.longitude })));
                  return (
                    <GoogleMap
                      mapContainerStyle={mapContainerStyle}
                      zoom={mapConfig.zoom}
                      center={mapConfig.center}
                      options={{
                        streetViewControl: false,
                        mapTypeControl: true,
                        fullscreenControl: true,
                        zoomControl: true,
                        minZoom: coverageMapZoomLevel === 'city' ? 8 : 4,
                        maxZoom: coverageMapZoomLevel === 'city' ? 14 : 10,
                      }}
                    >
                      {currentCoverageAreas.map((area, index) => (
                        <Circle
                          key={index}
                          center={{
                            lat: area.centerPoint.latitude,
                            lng: area.centerPoint.longitude
                          }}
                          radius={area.radiusKm * 1000} // Convert km to meters
                          options={{
                            fillColor: '#ff0000',
                            fillOpacity: 0.2,
                            strokeColor: '#ff0000',
                            strokeOpacity: 0.8,
                            strokeWeight: 2,
                          }}
                        />
                      ))}
                    </GoogleMap>
                  );
                })()}
              </div>
            ) : (
              <div className="p-4 bg-gray-50 border border-gray-300 rounded-md text-center">
                <p className="text-sm text-gray-600">
                  No coverage areas configured yet. Add coverage areas above and save to see them on the map.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Expansion Recommendations Section */}
        {isLoaded && (
          <div className="mt-8 border-t border-gray-300 pt-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-1">Expansion Recommendations</h3>
                <p className="text-sm text-gray-600">
                  Areas with high demand for driver services (based on rejected pickup locations from the last 90 days)
                </p>
                {totalRejectedRequests > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Total rejected requests: {totalRejectedRequests.toLocaleString()}
                  </p>
                )}
              </div>
              <button
                onClick={fetchRecommendations}
                disabled={loadingRecommendations}
                className="px-4 py-2 text-sm bg-gray-200 text-gray-800 rounded hover:bg-gray-300 disabled:opacity-50"
              >
                {loadingRecommendations ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin inline mr-2" />
                    Loading...
                  </>
                ) : (
                  'Refresh'
                )}
              </button>
            </div>

            {/* View Toggle and Settings for Rejected Locations */}
            <div className="mb-4 flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">View:</label>
                  <button
                    onClick={() => setRejectedLocationsView('dots')}
                    className={`px-3 py-1 text-sm rounded ${
                      rejectedLocationsView === 'dots'
                        ? 'bg-black text-white'
                        : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    Dots
                  </button>
                  <button
                    onClick={() => setRejectedLocationsView('bubbles')}
                    className={`px-3 py-1 text-sm rounded ${
                      rejectedLocationsView === 'bubbles'
                        ? 'bg-black text-white'
                        : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    Bubbles
                  </button>
                </div>
                
                {rejectedLocationsView === 'bubbles' && (
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-700">Bubble Radius (km):</label>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={bubbleRadiusKm}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value) || 5;
                        setBubbleRadiusKm(val);
                        // Clear bubbles when radius changes to trigger refetch
                        setRejectedLocationsBubbles([]);
                      }}
                      className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                    />
                  </div>
                )}
                {rejectedMapCache && rejectedMapCache.cached && (
                  <div className="text-xs text-gray-500 flex items-center gap-1" title="Rejected location data is cached. Map tiles still load from Google Maps API on zoom/pan.">
                    <span>✓ Data Cached</span>
                    {rejectedMapCache.lastUpdated && (
                      <span>({new Date(rejectedMapCache.lastUpdated).toLocaleTimeString()})</span>
                    )}
                  </div>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setRejectedMapZoomLevel('city');
                    checkCacheAndFetchRejectedLocations();
                  }}
                  className={`px-3 py-1 text-sm rounded ${
                    rejectedMapZoomLevel === 'city'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  City View
                </button>
                <button
                  onClick={() => {
                    setRejectedMapZoomLevel('state');
                    checkCacheAndFetchRejectedLocations();
                  }}
                  className={`px-3 py-1 text-sm rounded ${
                    rejectedMapZoomLevel === 'state'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  State View
                </button>
                <button
                  onClick={handleForceRefresh}
                  disabled={loadingRejectedLocations}
                  className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 flex items-center gap-1"
                  title="Force refresh (bypass cache)"
                >
                  <RefreshCw className={`h-3 w-3 ${loadingRejectedLocations ? 'animate-spin' : ''}`} />
                  Force Refresh
                </button>
              </div>
            </div>

            {loadingRecommendations || loadingRejectedLocations ? (
              <div className="flex justify-center items-center py-8 border border-gray-300 rounded-md">
                <Loader className="h-6 w-6 animate-spin text-black mr-2" />
                <span className="text-gray-600">Loading recommendations...</span>
              </div>
            ) : recommendations.length > 0 || rejectedLocationsRaw.length > 0 || rejectedLocationsBubbles.length > 0 ? (
              <div className="space-y-4">
                {/* Rejected Locations Map */}
                <div className="border border-gray-300 rounded-md overflow-hidden">
                  {(() => {
                    let mapItems: Array<{ centerLat?: number; centerLng?: number; latitude?: number; longitude?: number }> = [];
                    if (rejectedLocationsView === 'dots') {
                      mapItems = rejectedLocationsRaw.map(loc => ({ latitude: loc.latitude, longitude: loc.longitude }));
                    } else {
                      mapItems = rejectedLocationsBubbles.map(b => ({ centerLat: b.centerLat, centerLng: b.centerLng }));
                    }
                    const mapConfig = getMapCenterAndZoom(rejectedMapZoomLevel, mapItems);
                    const maxBubbleCount = rejectedLocationsBubbles.length > 0 
                      ? Math.max(...rejectedLocationsBubbles.map(b => b.requestCount), 1) 
                      : 1;

                    return (
                      <GoogleMap
                        mapContainerStyle={mapContainerStyle}
                        zoom={mapConfig.zoom}
                        center={mapConfig.center}
                        options={{
                          streetViewControl: false,
                          mapTypeControl: true,
                          fullscreenControl: true,
                          zoomControl: true,
                          minZoom: rejectedMapZoomLevel === 'city' ? 8 : 4,
                          maxZoom: rejectedMapZoomLevel === 'city' ? 14 : 10,
                        }}
                      >
                        {/* Show current coverage areas in red */}
                        {currentCoverageAreas.map((area, index) => (
                          <Circle
                            key={`coverage-${index}`}
                            center={{
                              lat: area.centerPoint.latitude,
                              lng: area.centerPoint.longitude
                            }}
                            radius={area.radiusKm * 1000}
                            options={{
                              fillColor: '#ff0000',
                              fillOpacity: 0.15,
                              strokeColor: '#ff0000',
                              strokeOpacity: 0.6,
                              strokeWeight: 2,
                            }}
                          />
                        ))}
                        
                        {/* Show rejected locations - Dots view ONLY when dots view is selected */}
                        {rejectedLocationsView === 'dots' && rejectedLocationsRaw.length > 0 && isLoaded && typeof window !== 'undefined' && window.google && rejectedLocationsRaw.map((loc, index) => {
                          // Small red pin icon as SVG data URL
                          const pinIconSvg = encodeURIComponent(`
                            <svg width="12" height="12" viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="6" cy="6" r="5" fill="#ef4444" stroke="#dc2626" stroke-width="1"/>
                            </svg>
                          `);
                          
                          return (
                            <Marker
                              key={`rejected-dot-${index}`}
                              position={{ lat: loc.latitude, lng: loc.longitude }}
                              icon={{
                                url: `data:image/svg+xml;charset=UTF-8,${pinIconSvg}`,
                                scaledSize: new window.google.maps.Size(12, 12),
                                anchor: new window.google.maps.Point(6, 6),
                              }}
                            />
                          );
                        })}
                        
                        {/* Show rejected locations - Bubbles view ONLY when bubbles view is selected */}
                        {rejectedLocationsView === 'bubbles' && rejectedLocationsBubbles.length > 0 && rejectedLocationsBubbles.map((bubble, index) => (
                          <Circle
                            key={`rejected-bubble-${index}`}
                            center={{ lat: bubble.centerLat, lng: bubble.centerLng }}
                            radius={bubble.radiusKm * 1000}
                            options={{
                              fillColor: getBubbleColor(bubble.requestCount, maxBubbleCount),
                              fillOpacity: 0.3,
                              strokeColor: '#dc2626',
                              strokeOpacity: 0.8,
                              strokeWeight: 2,
                            }}
                          />
                        ))}
                        
                        {/* Show recommended areas in blue (always shown, separate from rejected locations) */}
                        {recommendations.length > 0 && recommendations.map((rec, index) => (
                          <Circle
                            key={`recommendation-${index}`}
                            center={{
                              lat: rec.centerLat,
                              lng: rec.centerLng
                            }}
                            radius={5000} // 5km radius for visualization
                            options={{
                              fillColor: '#3b82f6',
                              fillOpacity: 0.2,
                              strokeColor: '#3b82f6',
                              strokeOpacity: 0.8,
                              strokeWeight: 2,
                            }}
                          />
                        ))}
                      </GoogleMap>
                    );
                  })()}
                </div>

                {/* Recommendations List */}
                <div className="space-y-3">
                  {recommendations.map((rec, index) => (
                    <div key={index} className="border border-gray-300 rounded-md p-4 bg-blue-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="px-2 py-1 bg-blue-600 text-white text-xs font-semibold rounded">
                              #{index + 1}
                            </span>
                            <h4 className="font-semibold text-gray-800">
                              {rec.sampleAddress || `${rec.centerLat.toFixed(4)}, ${rec.centerLng.toFixed(4)}`}
                            </h4>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                            <div>
                              <span className="text-gray-600">Requests (90 days): </span>
                              <span className="font-semibold text-black">{rec.requestCount}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Est. Monthly Bookings: </span>
                              <span className="font-semibold text-green-600">{rec.estimatedMonthlyBookings}</span>
                            </div>
                            {rec.nearestCity && (
                              <div>
                                <span className="text-gray-600">Nearest City: </span>
                                <span className="font-semibold">{rec.nearestCity}</span>
                              </div>
                            )}
                            {rec.avgDistanceToNearest !== undefined && (
                              <div>
                                <span className="text-gray-600">Avg Distance: </span>
                                <span className="font-semibold">{rec.avgDistanceToNearest.toFixed(1)} km</span>
                              </div>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            Last request: {new Date(rec.lastRequestDate).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="ml-4">
                          <button
                            onClick={() => {
                              // Prepare a pending coverage area (not added until confirmed)
                              const newArea: CoverageArea = {
                                cityName: rec.sampleAddress?.split(',')[0] || `Area ${index + 1}`,
                                centerPoint: {
                                  latitude: rec.centerLat,
                                  longitude: rec.centerLng
                                },
                                radiusKm: 10 // Default radius
                              };
                              setPendingCoverageArea(newArea);
                              setEditingArea(null);
                              setSearchAddress('');
                              setMapPickerKey(0);
                              setSelectedLocation({ lat: rec.centerLat, lng: rec.centerLng });
                              setShowMapPickerModal(-1); // Use -1 to indicate pending area
                            }}
                            className="px-3 py-1 text-xs bg-black text-white rounded hover:bg-gray-800"
                          >
                            Add Coverage
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="p-4 bg-gray-50 border border-gray-300 rounded-md text-center">
                <p className="text-sm text-gray-600">
                  No expansion recommendations available yet. Recommendations will appear as users try to book from locations outside your coverage area.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Map Picker Modal */}
        {showMapPickerModal !== null && (() => {
          const currentArea = getCurrentCoverageAreaForModal();
          return currentArea ? (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                      {showMapPickerModal === -1 ? 'Confirm Location for New Coverage Area' : `Select Center Point for ${currentArea.cityName}`}
                    </h3>
                    {showMapPickerModal === -1 && (
                      <div className="mt-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Area Name
                        </label>
                        <input
                          type="text"
                          value={currentArea.cityName}
                          onChange={(e) => setPendingCoverageArea({ ...currentArea, cityName: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                          placeholder="Enter area name"
                        />
                      </div>
                    )}
                  </div>
                  <button
                    onClick={handleCancelLocationSelection}
                    className="p-2 hover:bg-gray-100 rounded-md transition-colors ml-4"
                    aria-label="Close"
                  >
                    <XIcon className="h-5 w-5 text-gray-600" />
                  </button>
                </div>
                <div className="p-4 space-y-4">
                  {/* Location Search Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Search Location
                    </label>
                    <LocationSearchInputBackend
                      value={searchAddress}
                      onChange={(value) => setSearchAddress(value)}
                      placeholder="Search for a location..."
                      onPlaceSelected={handlePlaceSelected}
                    />
                  </div>
                  
                  {/* Map Picker */}
                  <div>
                    <MapPicker
                      key={mapPickerKey}
                      initialLocation={selectedLocation || { 
                        lat: currentArea.centerPoint.latitude || defaultCenter.lat, 
                        lng: currentArea.centerPoint.longitude || defaultCenter.lng 
                      }}
                      address={searchAddress || (currentArea.centerPoint ? 
                        `${currentArea.centerPoint.latitude.toFixed(6)}, ${currentArea.centerPoint.longitude.toFixed(6)}` : 
                        undefined)}
                      onLocationSelect={(location) => {
                        handleMapLocationSelect(showMapPickerModal, location);
                        // Update search address when map moves
                        if (location.address) {
                          setSearchAddress(location.address);
                        }
                      }}
                      onConfirm={() => handleConfirmLocation(showMapPickerModal)}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : null;
        })()}
    </div>
  );
};

export default CityCoverageManager;

